// src/pages/ProfilePage/ProfilePage.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  ProfilePageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ButtonWrapper,
  TariffCardsContainer,
  TableContainer,
  FeatureTable,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableData,
  TableDataCenter,
} from "./ProfilePage.styles";
import Button from "../../components/Buttons/Button";
import Sidebar from "../../components/Sidebar";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import {
  fetchTariffs,
  createPayment,
  fetchTariffFunctionMatrix,
} from "../../services/subscriptionService/subscriptionService";
import TariffCard from "../../components/TariffCard/TariffCard";
import { Tariff } from "../SubscriptionPage/SubscriptionPage.types";
import { Toast } from "primereact/toast";
import ConfirmModal from "../../components/Modals/ConfirmModal/ConfirmModal";
import { pxToRem } from "../../styles/utils";
import { TariffFunctionMatrix } from "../../services/subscriptionService/subscriptionService.types";
import successIcon from "../../assets/icons/successIcon.svg";

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [matrix, setMatrix] = useState<TariffFunctionMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFinalConfirmModalOpen, setIsFinalConfirmModalOpen] = useState(false);
  const [selectedTariffId, setSelectedTariffId] = useState<number | null>(null);

  const handleLogout = () => {
    Cookies.remove("access_token");
    Cookies.remove("refresh_token");
    Cookies.remove("user_id");
    localStorage.clear();
    navigate("/login");
    toastRef.current?.show({
      severity: "success",
      summary: "Успех",
      detail: "Вы успешно вышли из системы.",
      life: 3000,
    });
  };

  const handleTariffChange = (tariffId: number) => {
    setSelectedTariffId(tariffId);
    setIsFinalConfirmModalOpen(true);
  };

  const confirmTariffChange = async () => {
    setIsFinalConfirmModalOpen(false);
    if (!selectedTariffId) return;
    try {
      const paymentUrl = await createPayment(selectedTariffId);
      window.location.href = paymentUrl;
    } catch (error) {
      toastRef.current?.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось создать платеж. Попробуйте позже.",
        life: 5000,
      });
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [tariffsData, matrixData] = await Promise.all([
          fetchTariffs(),
          fetchTariffFunctionMatrix(),
        ]);
        setTariffs(tariffsData);
        setMatrix(matrixData);
      } catch (error) {
        console.error(error);
        toastRef.current?.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось загрузить данные.",
          life: 5000,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) return <p>Загрузка данных...</p>;

  return (
    <ProfilePageContainer>
      <Toast ref={toastRef} />
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <Title>Личный кабинет</Title>
          <ButtonWrapper>
            <Button
              variant="red"
              width={`${pxToRem(120)}`}
              height={`${pxToRem(50)}`}
              onClick={handleLogout}
            >
              Выход
            </Button>
          </ButtonWrapper>
        </HeaderContainer>

        <TariffCardsContainer>
          {tariffs.map((t) => (
            <TariffCard
              key={t.id}
              title={t.name}
              description={t.description}
              price={t.price}
              isActive={t.is_active}
              expirationDate={t.expiration_date}
              duration={t.duration_days}
              tariffId={t.id}
              onChangeTariff={() => handleTariffChange(t.id)}
              by_request={t.by_request}
            />
          ))}
        </TariffCardsContainer>

        {matrix && (
            <TableContainer>
              <FeatureTable>
                <TableHead>
                  <TableRow>
                    <TableHeader>Функция</TableHeader>
                    {matrix.tariffs.map((h) => (
                      <TableHeader key={h.id}>{h.name}</TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matrix.functions.map((f) => (
                    <TableRow key={f.id}>
                      <TableData>{f.name}</TableData>
                      {f.flags.map((has, i) => (
                        <TableDataCenter key={i}>
                          {has && (
                            <img
                              src={successIcon}
                              alt="✓"
                              style={{
                                width: pxToRem(25),
                                height: pxToRem(25),
                              }}
                            />
                          )}
                        </TableDataCenter>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </FeatureTable>
            </TableContainer>
        )}

        <ConfirmModal
          isOpen={isFinalConfirmModalOpen}
          title="Подтверждение"
          message="При смене тарифа подписка начинается заново"
          confirmButtonText="Обновить"
          onConfirm={confirmTariffChange}
          onCancel={() => setIsFinalConfirmModalOpen(false)}
          showAgreementLinks={true}
        />
      </ContentWrapper>
    </ProfilePageContainer>
  );
};

export default ProfilePage;
